import { useTranslation } from "react-i18next";

import { scriptErrorKey } from "../services/script";

/**
 * A field the API stores per locale — rendered as one input per language.
 * `value` is { en, ar }; `errors` is the 422 map, read at `${name}.en` / `.ar`.
 *
 * Each box also checks its own script as the user types, mirroring the API's
 * InLocaleScript rule: English letters in the Arabic box, or Arabic letters in
 * the English one, are called out at the keystroke instead of at the save. The
 * server stays the authority — a server error for the same box wins, because it
 * may be saying something this cannot know (a duplicate name, a length limit).
 *
 * Every bilingual form in the portal renders through here, so the check lands
 * on all of them at once rather than being copied into six modals.
 */
export default function TranslatableField({
  name,
  label,
  value,
  onChange,
  errors = {},
  required = false,
  textarea = false,
  rows = 3,
}) {
  const { t } = useTranslation();

  const field = (locale, dir) => {
    const id = `${name}-${locale}`;
    const serverError = errors[`${name}.${locale}`]?.[0];
    const scriptKey = scriptErrorKey(locale, value?.[locale]);
    const error = serverError ?? (scriptKey ? t(scriptKey) : undefined);
    const Tag = textarea ? "textarea" : "input";

    return (
      <div>
        <label className="field-label" htmlFor={id}>
          {label} <span className="text-xs text-ink-2">({t(`common.locales.${locale}`)})</span>
          {required && <span className="text-danger-text"> *</span>}
        </label>
        <Tag
          id={id}
          dir={dir}
          rows={textarea ? rows : undefined}
          aria-invalid={error ? true : undefined}
          className={`field-input ${error ? "border-danger" : ""}`}
          value={value?.[locale] ?? ""}
          onChange={(e) => onChange({ ...value, [locale]: e.target.value })}
        />
        {error && <p className="field-error">{error}</p>}
      </div>
    );
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {field("en", "ltr")}
      {field("ar", "rtl")}
    </div>
  );
}
